import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, Paper, Typography, Chip, Divider, FormControl, InputLabel, Select, MenuItem, Button, Tooltip, CircularProgress, LinearProgress, IconButton } from '@mui/material';
import AppsIcon from '@mui/icons-material/Apps';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, styled, alpha, lighten } from '@mui/material/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import dayjs from 'dayjs';
import { API_PATHS } from '../../../utils/constants';
import { getToken } from '../../../services/auth';

const formatDateISO = (d) => dayjs(d).toISOString();

const safeFetchJson = async (url, token, fallback = null) => {
  try {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return fallback;
    return await res.json();
  } catch (e) {
    return fallback;
  }
};

// Status -> colour/label for the breakdown — covers the statuses returned by
// /v1/dashboard/document-services/aggregate. Any other status falls back to teal.
const TICKET_STATUS_COLORS = {
  OPEN: '#3498db',
  IN_PROGRESS: '#f39c12',
  ASSIGNED: '#9b59b6',
  COMPLETED: '#27ae60',
  REJECTED: '#e74c3c',
  CLOSED: '#7f8c8d',
};
const statusColor = (status) => TICKET_STATUS_COLORS[String(status || '').toUpperCase()] || '#064d51';
const statusLabel = (status) => String(status || '')
  .replace(/_/g, ' ')
  .toLowerCase()
  .replace(/\b\w/g, (c) => c.toUpperCase());

// Register chart components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  ChartTitle,
  ChartTooltip,
  ChartLegend
);

// Styled components to match Customer dashboard
const ChartCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  height: '100%',
}));

const DataTable = styled((props) => <Box component="table" {...props} />)(({ theme }) => ({
  width: '100%',
  borderCollapse: 'collapse',
  '& th, & td': { padding: '8px 12px', fontSize: '0.875rem', borderBottom: '1px solid #eee' },
  '& thead th': { backgroundColor: '#064d51', color: '#ffffff', fontWeight: 600 },
  '& tbody tr:nth-of-type(even)': { backgroundColor: 'rgba(192, 242, 243, 0.1)' },
}));

export default function InsightsDashboard() {
  const theme = useTheme();
  const [range, setRange] = useState('7d');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [statusBreakdown, setStatusBreakdown] = useState(null);
  const [services, setServices] = useState([]);
  const [aging, setAging] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [trends, setTrends] = useState([]);
  const [assignmentSplit, setAssignmentSplit] = useState(null);
  const [error, setError] = useState(null);
  const [orgName, setOrgName] = useState('Organization');

  // Dashboard variables (UI only for now)
  const [org, setOrg] = useState('current');
  const [svcGroup, setSvcGroup] = useState('all');
  const [svc, setSvc] = useState('all');
  const [assignee, setAssignee] = useState('all');
  const [showThaklisCompare, setShowThaklisCompare] = useState(true);
  const [showAllAssignees, setShowAllAssignees] = useState(false);
  const [showAllServices, setShowAllServices] = useState(false);

  const token = useMemo(() => getToken(), []);
  // Responsive helpers for Services single-row view
  const isXlUp = useMediaQuery(theme.breakpoints.up('xl'));
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const servicesMaxVisible = isXlUp ? 6 : isLgUp ? 6 : isMdUp ? 4 : isSmUp ? 2 : 1;

  const { from, to } = useMemo(() => {
    const now = dayjs();
    switch (range) {
      case 'today':
        return { from: now.startOf('day').toISOString(), to: now.toISOString() };
      case '30d':
        return { from: now.subtract(30, 'day').toISOString(), to: now.toISOString() };
      case '7d':
      default:
        return { from: now.subtract(7, 'day').toISOString(), to: now.toISOString() };
    }
  }, [range]);

  const orgId = null; // If you have current org context, set its UUID here. Otherwise backend should infer from token.

  useEffect(() => {
    let mounted = true;
    if (!token) return;
    const q = new URLSearchParams();
    if (orgId) q.set('orgId', orgId);
    q.set('from', from);
    q.set('to', to);

    const run = async () => {
      setLoading(true);
      setError(null);
      // Single aggregate API call (plus org details for label)
      const base = API_PATHS.V1 + '/dashboard';
      // fetch current org details for display label
      const orgDetails = await safeFetchJson(API_PATHS.V1 + '/organisation/details', token, null);
      if (mounted && orgDetails && orgDetails.name) {
        setOrgName(orgDetails.name);
      }
      const agg = await safeFetchJson(`${base}/insights/aggregate?${q.toString()}`, token, null);
      if (!mounted) return;
      const useSummary   = agg?.summary || null;
      const useStatus    = agg?.ticketsByStatus || null;
      const useServices  = (Array.isArray(agg?.services) && agg.services.length > 0) ? agg.services : [];
      const useAging     = agg?.aging || null;
      const useAssignees = (Array.isArray(agg?.assignees) && agg.assignees.length > 0) ? agg.assignees : [];
      const useTrends    = (Array.isArray(agg?.trends) && agg.trends.length > 0) ? agg.trends : [];

      setSummary(useSummary);
      setStatusBreakdown(useStatus);
      setServices(useServices);
      setAging(useAging);
      setAssignees(useAssignees);
      setTrends(useTrends);
      setAssignmentSplit(agg?.assignmentSplit || null);
      setLoading(false);
    };
    run();
    return () => { mounted = false; };
  }, [token, from, to, orgId]);

  const k = (obj, path, d = 0) => {
    try {
      return path.split('.').reduce((p, c) => (p && p[c] !== undefined ? p[c] : d), obj);
    } catch {
      return d;
    }
  };

  // Brand-tuned amber/yellow (softer than default warning)
  // Brand palette alignment with other dashboards
  const brandWarning = '#f39c12';
  const brandGreen = '#27ae60';
  const brandRed = '#e74c3c';
  const brandTeal = '#064d51';

  // Rotate service card accents similar to Document Types palette
  const serviceColors = [
    '#064d51', '#0a7c82', '#c0f2f3', '#3498db', '#27ae60', '#f39c12'
  ];

  // Build trends line chart dataset from API response
  const trendChart = useMemo(() => {
    if (!trends || trends.length === 0) return null;
    const labels = trends.map((t) => t.date);
    const created = trends.map((t) => t.created || 0);
    const resolved = trends.map((t) => t.resolved || 0);
    return {
      labels,
      datasets: [
        {
          label: 'Created',
          data: created,
          borderColor: theme.palette.primary.main,
          backgroundColor: alpha(theme.palette.primary.main, 0.2),
          tension: 0.3,
          fill: true,
        },
        {
          label: 'Resolved',
          data: resolved,
          borderColor: theme.palette.success.main,
          backgroundColor: alpha(theme.palette.success.main, 0.2),
          tension: 0.3,
          fill: true,
        },
      ],
    };
  }, [trends, theme]);

  // Build bar for assignment split — ticket counts assigned to the org vs Thaklis.
  const assignmentChart = useMemo(() => {
    if (!assignmentSplit) return null;
    const org = assignmentSplit.organization || 0;
    const th  = assignmentSplit.thaklis || 0;
    return {
      labels: [orgName || 'Organization', 'Thaklis'],
      datasets: [
        {
          label: 'Tickets',
          data: [org, th],
          backgroundColor: [alpha(theme.palette.info.main, 0.6), alpha(brandTeal, 0.6)],
          borderColor: [theme.palette.info.main, brandTeal],
          borderWidth: 1,
          borderRadius: 4,
        }
      ]
    };
  }, [assignmentSplit, theme, orgName]);


  const Badge = ({ label, color }) => (
    <Box sx={{
      display: 'inline-flex',
      alignItems: 'center',
      border: `1px solid ${color || theme.palette.grey[300]}`,
      color: color || theme.palette.text.primary,
      borderRadius: 1.5,
      px: 1,
      py: 0.25,
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 1.2,
      backgroundColor: '#fff'
    }}>{label}</Box>
  );

  const Panel = ({ title, children, right }) => (
    <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ color: '#064d51', fontWeight: 600 }}>{title}</Typography>
        <Box sx={{ ml: 'auto' }}>{right}</Box>
      </Box>
      {children}
    </Paper>
  );

  // Compute total created in current range using trends when not 'today'
  const createdInRange = useMemo(() => {
    if (!trends || trends.length === 0) return 0;
    return trends.reduce((acc, t) => acc + (t.created || 0), 0);
  }, [trends]);

  return (
    <Box sx={{ p: 3, bgcolor: '#F5F5F5', minHeight: '100vh' }}>
      <Typography variant="h5" sx={{ my: 2, color: '#064d51', fontWeight: 600 }}>
        Document Services Dashboard
      </Typography>

      {/* Variables Bar */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.25, flexWrap: 'wrap' }}>
        {/* Compact Segmented Control */}
        <Box sx={{
          display: 'inline-flex',
          alignItems: 'center',
          borderRadius: 999,
          border: `1px solid ${alpha(theme.palette.grey[600], 0.25)}`,
          overflow: 'hidden',
          bgcolor: '#fff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
        }}>
          {[
            { key: 'today', label: 'Today' },
            { key: '7d', label: 'Last 7 days' },
            { key: '30d', label: 'Last 30 days' }
          ].map((opt, idx, arr) => (
            <Button
              key={opt.key}
              onClick={() => setRange(opt.key)}
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.82rem',
                px: 1.5,
                py: 0.4,
                borderRadius: 0,
                color: range === opt.key ? '#fff' : theme.palette.text.primary,
                bgcolor: range === opt.key ? alpha(theme.palette.primary.main, 0.95) : 'transparent',
                '&:hover': { bgcolor: range === opt.key ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.04) },
                ...(idx !== arr.length - 1 ? { borderRight: `1px solid ${alpha(theme.palette.grey[600], 0.2)}` } : {})
              }}
            >
              {opt.label}
            </Button>
          ))}
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="textSecondary">Loading…</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {[
          {
            label: range === 'today' ? 'Tickets Created Today' : (range === '30d' ? 'Tickets Created (30d)' : 'Tickets Created (7d)'),
            value: range === 'today' ? k(summary, 'tickets.createdToday') : createdInRange,
          },
          { label: 'Open', value: k(summary, 'tickets.open') },
          { label: 'In Progress', value: k(summary, 'tickets.inProgress') },
          { label: 'Completed', value: k(summary, 'tickets.completed') },
        ].map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <Paper sx={{ p: 2, borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}` }}>
              <Typography variant="overline" color="textSecondary">{card.label}</Typography>
              <Typography variant="h5" sx={{ lineHeight: 1.1, color: '#064d51', fontWeight: 600 }}>{card.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Status Breakdown (segmented bar) and Aging */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Panel title="Status Breakdown">
            {statusBreakdown ? (
              (() => {
                const entries = Object.entries(statusBreakdown);
                const total = Math.max(1, entries.reduce((acc, [, v]) => acc + (v || 0), 0));
                return (
                  <Box>
                    <Box sx={{ display: 'flex', height: 12, borderRadius: 8, overflow: 'hidden', mb: 1 }}>
                      {entries.map(([key, val]) => {
                        const soft = alpha(statusColor(key), 0.6);
                        return <Box key={key} sx={{ flex: (val || 0) / total, bgcolor: soft }} />;
                      })}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {entries.map(([key, val]) => (
                        <Badge key={key} label={`${statusLabel(key)}: ${val}`} color={statusColor(key)} />
                      ))}
                    </Box>
                  </Box>
                );
              })()
            ) : (
              <Typography variant="body2" color="textSecondary">No data yet.</Typography>
            )}
          </Panel>
        </Grid>
        <Grid item xs={12} md={4}>
          <Panel title="Aging">
            {(() => {
              const hasAging = !!aging && typeof aging === 'object';
              const lt2d = hasAging ? aging.lt2d : 0;
              const d3_7 = hasAging ? aging.d3_7 : 0;
              const gt7d = hasAging ? aging.gt7d : 0;
              const bucket = [
                { label: '< 2 days', value: lt2d, color: theme.palette.success.main },
                { label: '3–7 days', value: d3_7, color: brandWarning },
                { label: '> 7 days', value: gt7d, color: theme.palette.error.main },
              ];
              const max = Math.max(...bucket.map(b => b.value), 1);
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {bucket.map(b => (
                    <Box key={b.label}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption">{b.label}</Typography>
                        <Typography variant="caption">{b.value}</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={100 * b.value / max} sx={{ height: 8, borderRadius: 6, bgcolor: alpha(b.color, 0.15), [`& .MuiLinearProgress-bar`]: { backgroundColor: alpha(b.color, 0.7) } }} />
                    </Box>
                  ))}
                </Box>
              );
            })()}
          </Panel>
        </Grid>
      </Grid>

      {/* Trends + Assignment Split (two halves) */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={6}>
          <Panel title="Ticket Trends (Created vs Resolved)">
            {trendChart ? (
              <Box sx={{ height: 220 }}>
                <Line
                  data={trendChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'top' } },
                    scales: {
                      x: { grid: { display: false } },
                      y: { beginAtZero: true }
                    }
                  }}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">No trend data.</Typography>
            )}
          </Panel>
        </Grid>
        <Grid item xs={12} md={6}>
          <Panel title={`Tickets by Assignment (${orgName} vs Thaklis)`}>
            {assignmentChart ? (
              <Box sx={{ height: 220 }}>
                <Bar
                  data={assignmentChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'top' } },
                    scales: {
                      x: { grid: { display: false } },
                      y: { beginAtZero: true }
                    }
                  }}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">No assignment data.</Typography>
            )}
          </Panel>
        </Grid>
      </Grid>

      {/* Services */}
      <Panel
        title="Services"
        right={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {(() => {
              const total = services.length;
              const visible = showAllServices ? total : Math.min(servicesMaxVisible, total);
              const remaining = Math.max(0, total - visible);
              if (showAllServices) {
                return (
                  <Tooltip title="Show Less" arrow>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => setShowAllServices(false)}
                      sx={{
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) }
                      }}
                    >
                      <CloseFullscreenIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                );
              }
              return remaining > 0 ? (
                <Tooltip title={`View All (${remaining})`} arrow>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => setShowAllServices(true)}
                    sx={{
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) }
                    }}
                  >
                    <AppsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null;
            })()}
          </Box>
        )}
      >
        {showAllServices ? (
          <Grid container spacing={1.5}>
            {services.map(svc => {
              const open = svc.open || 0;
              const inProgress = svc.inProgress || 0;
              const completed = svc.completed || 0;
              const onHold = svc.onHold || 0;
              const cancelled = svc.cancelled || 0;
              const total = open + inProgress + completed + onHold + cancelled;
              return (
                <Grid key={svc.serviceId || svc.id} item xs={12} sm={6} md={3} lg={2} xl={2}>
                  <Paper sx={{ p: 1.25, borderRadius: 2, height: 92, display: 'flex', flexDirection: 'column', justifyContent: 'center', transition: 'box-shadow .2s, transform .2s', '&:hover': { boxShadow: '0 4px 14px rgba(0,0,0,0.12)', transform: 'translateY(-1px)' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Tooltip title={svc.serviceName || svc.name || 'Unnamed Service'} arrow>
                          <Typography variant="body2" sx={{ fontWeight: 400, fontFamily: theme.typography.fontFamily, color: theme.palette.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: 0.1 }}>
                            {svc.serviceName || svc.name || 'Unnamed Service'}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Typography variant="subtitle1" sx={{ ml: 1, lineHeight: 1.1, color: '#064d51', fontWeight: 700, letterSpacing: 0.2 }}>{total}</Typography>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
            {(!services || services.length === 0) && (
              <Grid item xs={12}><Typography variant="body2" color="textSecondary">No service data.</Typography></Grid>
            )}
          </Grid>
        ) : (
          <Grid container spacing={1.5}>
            {(services || []).slice(0, servicesMaxVisible).map(svc => {
              const open = svc.open || 0;
              const inProgress = svc.inProgress || 0;
              const completed = svc.completed || 0;
              const onHold = svc.onHold || 0;
              const cancelled = svc.cancelled || 0;
              const total = open + inProgress + completed + onHold + cancelled;
              return (
                <Grid key={svc.serviceId || svc.id} item xs={12} sm={6} md={3} lg={2} xl={2}>
                  <Paper sx={{ p: 1.25, borderRadius: 2, height: 92, display: 'flex', alignItems: 'center', transition: 'box-shadow .2s, transform .2s', '&:hover': { boxShadow: '0 4px 14px rgba(0,0,0,0.12)', transform: 'translateY(-1px)' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Tooltip title={svc.serviceName || svc.name || 'Unnamed Service'} arrow>
                          <Typography variant="body2" sx={{ fontWeight: 400, fontFamily: theme.typography.fontFamily, color: theme.palette.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {svc.serviceName || svc.name || 'Unnamed Service'}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Typography variant="subtitle1" sx={{ ml: 1, lineHeight: 1.1, color: '#064d51', fontWeight: 700, letterSpacing: 0.2 }}>{total}</Typography>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
            {(!services || services.length === 0) && (
              <Grid item xs={12}><Typography variant="body2" color="textSecondary">No service data.</Typography></Grid>
            )}
          </Grid>
        )}
      </Panel>

      {/* Assignees */}
      <Panel
        title="Assignees"
        right={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {(() => {
              const total = assignees.length;
              const visible = showAllAssignees ? total : Math.min(5, total);
              const remaining = Math.max(0, total - visible);
              if (showAllAssignees) {
                return (
                  <Tooltip title="Show Less" arrow>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => setShowAllAssignees(false)}
                      sx={{
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) }
                      }}
                    >
                      <CloseFullscreenIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                );
              }
              return remaining > 0 ? (
                <Tooltip title={`View All (${remaining})`} arrow>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => setShowAllAssignees(true)}
                    sx={{
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) }
                    }}
                  >
                    <AppsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null;
            })()}
          </Box>
        )}
      >
        <Grid container spacing={2}>
          {(showAllAssignees ? assignees : assignees.slice(0, 5)).map(a => (
            <Grid key={`${a.assigneeType}-${a.assigneeId}-${a.name}`} item xs={12} sm={6} md={4}>
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {a.name} {a.assigneeType ? <Chip label={a.assigneeType} size="small" sx={{ ml: 1 }} /> : null}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Badge label={`Open: ${a.open || 0}`} color={brandWarning} />
                  <Badge label={`In Progress: ${a.inProgress || 0}`} color={theme.palette.primary.main} />
                  <Badge label={`Completed: ${a.completed || 0}`} color={theme.palette.success.main} />
                </Box>
              </Paper>
            </Grid>
          ))}
          {(!assignees || assignees.length === 0) && (
            <Grid item xs={12}><Typography variant="body2" color="textSecondary">No assignee data.</Typography></Grid>
          )}
        </Grid>
      </Panel>
    </Box>
  );
}
